import * as z from "zod";
import {
  regexAlphaNumericPlus,
  regexAlphaPlus,
  regexInternationalPostcode,
  regexUkPostcode,
} from "@fwa/src/utils/formUtils";

export const addressValidation = z
  .object({
    line1: z
      .string({ required_error: "Address line 1 field is required." })
      .trim()
      .min(1, "Address line 1 field is required.")
      .min(3, "Address line 1 should be at least 3 characters long.")
      .max(100, "Address line 1 should be at most 100 characters long.")
      .regex(
        regexAlphaNumericPlus.pattern,
        `Address line 1 ${regexAlphaNumericPlus.message}`,
      )
      .refine(
        (value) => (value ? !regexUkPostcode.pattern.test(value) : true),
        "Address line 1 should not contain a postcode.",
      ),
    line2: z
      .string()
      .trim()
      .max(100, "Address line 2 should be at most 100 characters long.")
      .regex(
        regexAlphaNumericPlus.pattern,
        `Address line 2 ${regexAlphaNumericPlus.message}`,
      )
      .optional(),
    line3: z
      .string()
      .trim()
      .max(100, "Address line 3 should be at most 100 characters long.")
      .regex(
        regexAlphaNumericPlus.pattern,
        `Address line 3 ${regexAlphaNumericPlus.message}`,
      )
      .optional(),
    city: z
      .string()
      .trim()
      .min(1, "City field is required.")
      .min(2, "City should be at least 2 characters long.")
      .max(50, "City should be at most 50 characters long.")
      .regex(regexAlphaPlus.pattern, `City ${regexAlphaPlus.message}`),
    country: z
      .string()
      .trim()
      .max(50, "Country should be at most 50 characters long.")
      .regex(
        regexAlphaNumericPlus.pattern,
        `City ${regexAlphaNumericPlus.message}`,
      ),
    postalCode: z
      .string()
      .trim()
      .min(1, "Postcode field is required.")
      .min(2, "Postcode should be at least 2 characters long."),
    validated: z.boolean(),
  })
  .refine(
    // line1 should not be empty when line2 isn't
    (data) => (data.line2?.length && !data.line1?.length ? false : true),
    {
      message: "Address line 1 missing",
      path: ["line1"],
    },
  )
  .refine(
    // Line2 should not be empty when line3 isn't
    (data) => (data.line3?.length && !data.line2?.length ? false : true),
    {
      message: "Address line 2 missing",
      path: ["line2"],
    },
  )
  .refine(
    // line1 should match line2 or line3
    (data) =>
      data.line1?.length &&
      (data.line1 === data.line2 || data.line1 === data.line3)
        ? false
        : true,
    {
      message:
        "Address line 1, Address line 2, Address line 3 should not match.",
      path: ["line1"],
    },
  )
  .refine(
    // line2 should match line1 or line3
    (data) =>
      data.line2?.length &&
      (data.line2 === data.line1 || data.line2 === data.line3)
        ? false
        : true,
    {
      message:
        "Address line 1, Address line 2, Address line 3 should not match.",
      path: ["line2"],
    },
  )
  .refine(
    // line3 should match line1 or line2
    (data) =>
      data.line3?.length &&
      (data.line3 === data.line1 || data.line3 === data.line2)
        ? false
        : true,
    {
      message:
        "Address line 1, Address line 2, Address line 3 should not match.",
      path: ["line3"],
    },
  )
  .refine(
    // if UK country postcode should be valid length
    (data) =>
      data.postalCode &&
      (data.postalCode?.length || 0) > 8 &&
      ["GBR", "GGY", "IMN", "JEY"].includes(data.country)
        ? false
        : true,
    {
      message: "Postcode should be at most 8 characters long.",
      path: ["postalCode"],
    },
  )
  .refine(
    // if non UK country postcode should be valid length
    (data) =>
      data.postalCode &&
      (data.postalCode?.length || 0) > 10 &&
      !["GBR", "GGY", "IMN", "JEY"].includes(data.country)
        ? false
        : true,
    {
      message: "Postcode should be at most 10 characters long.",
      path: ["postalCode"],
    },
  )
  .refine(
    // if UK country postcode should be valid postcode
    (data) =>
      data.postalCode && ["GBR", "GGY", "IMN", "JEY"].includes(data.country)
        ? regexUkPostcode.pattern.test(data.postalCode)
        : true,
    {
      message: regexUkPostcode.message,
      path: ["postalCode"],
    },
  )
  .refine(
    // if non UK country postcode should be valid postcode
    (data) =>
      data.postalCode && ["GBR", "GGY", "IMN", "JEY"].includes(data.country)
        ? true
        : regexInternationalPostcode.pattern.test(data.postalCode),
    {
      message: `Postcode ${regexInternationalPostcode.message}`,
      path: ["postalCode"],
    },
  );

export default addressValidation;
